import { useMemo, useEffect } from 'react'
import { useTranslation } from '@flight-tools/i18n'
import { Card, CardHeader, CardTitle, CardContent, Separator, Badge, cn } from '@flight-tools/ui'
import { AirplaneTilt, Baby } from '@phosphor-icons/react'
import { motion, AnimatePresence } from 'framer-motion'
import type { CabinLayout, Seat } from '@flight-tools/domain'

import en from './locales/en.json'
import es from './locales/es.json'
import sv from './locales/sv.json'

interface CabinLayoutViewProps {
  layout: CabinLayout
}

interface SeatTileProps {
  seat: Seat
  delay: number
}

function SeatTile({ seat, delay }: SeatTileProps) {
  const isBlocked = seat.isBlocked
  const isOccupied = seat.isOccupied
  const isBusiness = seat.cabinClass === 'business'

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay }}
      className={cn(
        'relative flex h-10 w-10 items-center justify-center rounded-lg text-xs font-mono font-semibold transition-all duration-200',
        isBlocked && 'border-2 border-dashed border-muted-foreground/30 bg-transparent opacity-30',
        !isBlocked &&
          !isOccupied &&
          'border-2 border-border bg-muted/30 hover:border-primary hover:bg-primary/10 hover:scale-110 cursor-pointer',
        !isBlocked &&
          isOccupied &&
          isBusiness &&
          'bg-gradient-to-br from-accent to-accent/80 text-accent-foreground shadow-lg shadow-accent/30',
        !isBlocked &&
          isOccupied &&
          !isBusiness &&
          'bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/30'
      )}
    >
      {!isBlocked && seat.letter}
      
      {/* Lap Infant Icon */}
      <AnimatePresence>
        {seat.hasLapInfant && (
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-pink-500 text-white"
          >
            <Baby weight="fill" className="h-3 w-3" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

interface RowProps {
  rowNumber: number
  seats: Seat[]
  rowIndex: number
}

function Row({ rowNumber, seats, rowIndex }: RowProps) {
  const leftSeats = seats.filter((s) => ['A', 'B', 'C'].includes(s.letter))
  const rightSeats = seats.filter((s) => ['D', 'E', 'F'].includes(s.letter))

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: rowIndex * 0.02 }}
      className="flex items-center gap-3"
    >
      {/* Row Number */}
      <div className="w-8 text-center font-mono text-sm text-muted-foreground">{rowNumber}</div>

      {/* Left Seats (A, B, C) */}
      <div className="flex gap-1.5">
        {leftSeats.map((seat, idx) => (
          <SeatTile key={`${seat.row}-${seat.letter}`} seat={seat} delay={rowIndex * 0.02 + idx * 0.01} />
        ))}
      </div>

      {/* Aisle */}
      <div className="w-6" />

      {/* Right Seats (D, E, F) */}
      <div className="flex gap-1.5">
        {rightSeats.map((seat, idx) => (
          <SeatTile key={`${seat.row}-${seat.letter}`} seat={seat} delay={rowIndex * 0.02 + idx * 0.01 + 0.03} />
        ))}
      </div>
    </motion.div>
  )
}

export function CabinLayoutView({ layout }: CabinLayoutViewProps) {
  const { t, i18n } = useTranslation('cabin-layout')

  // Load colocated translations
  useEffect(() => {
    i18n.addResourceBundle('en', 'cabin-layout', en, true, true)
    i18n.addResourceBundle('es', 'cabin-layout', es, true, true)
    i18n.addResourceBundle('sv', 'cabin-layout', sv, true, true)
  }, [i18n])

  // Group seats by row
  const seatsByRow = useMemo(() => {
    const grouped = new Map<number, Seat[]>()
    for (const seat of layout.seats) {
      const existing = grouped.get(seat.row) ?? []
      existing.push(seat)
      grouped.set(seat.row, existing)
    }
    return grouped
  }, [layout.seats])

  // Separate business and economy rows
  const { businessRows, economyRows } = useMemo(() => {
    const business: Array<{ rowNumber: number; seats: Seat[] }> = []
    const economy: Array<{ rowNumber: number; seats: Seat[] }> = []

    for (const [rowNumber, seats] of seatsByRow) {
      const firstSeat = seats[0]
      if (firstSeat?.cabinClass === 'business') {
        business.push({ rowNumber, seats })
      } else {
        economy.push({ rowNumber, seats })
      }
    }

    return { businessRows: business, economyRows: economy }
  }, [seatsByRow])

  const isEmpty = layout.occupancy.occupiedSeats === 0

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AirplaneTilt weight="duotone" className="h-6 w-6 text-primary" />
          {t('cabinLayout')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <div className="flex h-64 flex-col items-center justify-center text-center">
            <AirplaneTilt weight="duotone" className="h-16 w-16 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">{t('emptyState')}</p>
          </div>
        ) : (
          <div className="space-y-4 overflow-x-auto pb-4">
            {/* Seat Labels */}
            <div className="flex items-center gap-3">
              <div className="w-8" />
              <div className="flex gap-1.5">
                {['A', 'B', 'C'].map((letter) => (
                  <div key={letter} className="flex h-6 w-10 items-center justify-center text-xs font-medium text-muted-foreground">
                    {letter}
                  </div>
                ))}
              </div>
              <div className="w-6" />
              <div className="flex gap-1.5">
                {['D', 'E', 'F'].map((letter) => (
                  <div key={letter} className="flex h-6 w-10 items-center justify-center text-xs font-medium text-muted-foreground">
                    {letter}
                  </div>
                ))}
              </div>
            </div>

            {/* Business Class Section */}
            {businessRows.length > 0 && (
              <div className="space-y-2">
                <Badge variant="business" className="mb-2">
                  {t('businessClass')}
                </Badge>
                {businessRows.map(({ rowNumber, seats }, idx) => (
                  <Row
                    key={rowNumber}
                    rowNumber={rowNumber}
                    seats={seats}
                    rowIndex={idx}
                  />
                ))}
              </div>
            )}

            {/* Cabin Separator */}
            {businessRows.length > 0 && economyRows.length > 0 && (
              <Separator className="my-4" />
            )}

            {/* Economy Class Section */}
            {economyRows.length > 0 && (
              <div className="space-y-2">
                <Badge variant="economy" className="mb-2">
                  {t('economyClass')}
                </Badge>
                {economyRows.map(({ rowNumber, seats }, idx) => (
                  <Row
                    key={rowNumber}
                    rowNumber={rowNumber}
                    seats={seats}
                    rowIndex={businessRows.length + idx}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
